import { Node, canonKey, display, evaluate, isTrivial, wholeOperands } from "./expression.ts";
import { equals, rat } from "./rational.ts";
import { Rng, createRng } from "./rng.ts";
import { DEFAULT_LEVEL, GRADES, GradeSpec, LEVELS, Level, gradeOf } from "./grades.ts";

export { DEFAULT_LEVEL, LEVELS, gradeOf };
export type { Level };

/*
 * Equal Groups (ADR 0127): sixteen tiles, four hidden groups of four, every tile
 * in a group worth the same whole number. The generator picks four distinct
 * target values, then writes four expressions for each, so the board has exactly
 * one solution by construction and needs no solver.
 *
 * What makes a board a puzzle and not a page of sums (ADR 0127 §2):
 *   - the four targets sit in a narrow window, so a tile's size gives nothing away;
 *   - each group mixes operations (grades 2-4) or kinds of expression (5-8);
 *   - the choice among expressions leans toward numbers that already appear in
 *     other groups, so "7 × 2" and "7 + 5" can share a board.
 *
 * What a grade's board is made of is the table in grades.ts (ADR 0127 §9).
 *
 * Every random number comes from rng.ts. The call sequence below is what a date
 * means: reordering or adding a draw changes boards already played. A change goes
 * in as a new entry of GENERATOR_VERSIONS, applying from its start date.
 */

export interface Tile {
    /** Stable for a board: the same tile has the same id on every visit. */
    id: string;
    text: string;
    groupIndex: number;
}

export interface Group {
    value: number;
    tiles: Tile[];
}

export interface Board {
    seed: string;
    level: Level;
    /** Four groups, lowest value first. */
    groups: Group[];
    /** The sixteen tile ids in the order the grid first shows them. */
    order: string[];
}

/** A board before its tiles are given text: what the tests check the arithmetic on. */
export interface BuiltBoard {
    groups: { value: number; nodes: Node[] }[];
    order: string[];
}

// ── Dates ────────────────────────────────────────────────────────────────────

/** Puzzle #1. */
export const LAUNCH_DATE = "2026-10-29";

/** The player's local date as YYYY-MM-DD (a class in one town sees one board). */
export function localDateKey(now: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function dailySeed(date: string, level: Level): string {
    return `${date}:${level}`;
}

function dateParts(date: string): [number, number, number] {
    const [year, month, day] = date.split("-").map(Number);
    return [year, month, day];
}

/** Whole days since launch, plus one. From date parts, so a clock change can't skew it. */
export function puzzleNumber(date: string): number {
    const [year, month, day] = dateParts(date);
    const [launchYear, launchMonth, launchDay] = dateParts(LAUNCH_DATE);
    const days = (Date.UTC(year, month - 1, day) - Date.UTC(launchYear, launchMonth - 1, launchDay)) / 86400000;
    return Math.round(days) + 1;
}

/** The date key `days` before (negative: after) `date`. */
export function shiftDate(date: string, days: number): string {
    const [year, month, day] = dateParts(date);
    return localDateKey(new Date(year, month - 1, day - days));
}

// ── Choosing the expressions ─────────────────────────────────────────────────

interface Candidate {
    node: Node;
    canon: string;
    /** The distinct whole numbers it is made of (sign ignored), for "shared with another group". */
    keys: string[];
    /** `keys` as one string: two tiles of a group with the same one read as the same fact twice. */
    kit: string;
}

interface Slot {
    group: number;
    candidates: Candidate[];
    chosen: Candidate | null;
}

/** A pull toward numbers already on the board: each shared number is worth this many extra. */
const SHARE_WEIGHT = 10;

const range = (from: number, to: number): number[] => {
    const out: number[] = [];
    for (let i = from; i <= to; i++) {
        out.push(i);
    }
    return out;
};

/** The expressions that really equal `value` and are worth a tile. */
function usable(value: number, nodes: Node[]): Candidate[] {
    const target = rat(value);
    const out: Candidate[] = [];
    for (const node of nodes) {
        if (!equals(evaluate(node), target) || isTrivial(node)) {
            continue;
        }
        const keys = [...new Set(wholeOperands(node).map((value) => String(Math.abs(value))))];
        out.push({ node, canon: canonKey(node), keys, kit: [...keys].sort().join(",") });
    }
    return out;
}

/**
 * `usable`, remembered: the list for a kind of expression and a value never changes, and the
 * biggest (order of operations) is hundreds of trees. Nothing chosen from it is altered.
 */
const usableCache = new Map<string, Candidate[]>();
function usableFor(kind: string, value: number, nodes: () => Node[]): Candidate[] {
    const key = `${kind}:${value}`;
    let found = usableCache.get(key);
    if (!found) {
        found = usable(value, nodes());
        usableCache.set(key, found);
    }
    return found;
}

/** Fills every slot, preferring numbers shared with other groups. False if a slot ran dry. */
function fill(slots: Slot[], rng: Rng): boolean {
    for (let pass = 0; pass < 3; pass++) {
        for (const index of rng.shuffle(range(0, slots.length - 1))) {
            const slot = slots[index];
            slot.chosen = null;
            const taken = new Set<string>();
            const sameKit = new Set<string>();
            const elsewhere = new Set<string>();
            for (const other of slots) {
                if (!other.chosen) {
                    continue;
                }
                taken.add(other.chosen.canon);
                if (other.group !== slot.group) {
                    other.chosen.keys.forEach((key) => elsewhere.add(key));
                } else if (other.chosen.kit) {
                    sameKit.add(other.chosen.kit);
                }
            }
            const free = slot.candidates.filter(
                (candidate) => !taken.has(candidate.canon) && !sameKit.has(candidate.kit)
            );
            if (free.length === 0) {
                return false;
            }
            slot.chosen = rng.weighted(
                free,
                (candidate) => 1 + SHARE_WEIGHT * candidate.keys.filter((key) => elsewhere.has(key)).length
            );
        }
    }
    return true;
}

function sorted(values: number[]): number[] {
    return [...values].sort((a, b) => a - b);
}

/**
 * Four distinct whole values in one of the grade's windows. A board with negative values has
 * one to three of them, in a window that starts at -4 or lower; the others have none.
 */
function targetsFor(spec: GradeSpec, rng: Rng, withNegative: boolean): number[] {
    for (let attempt = 0; attempt < 200; attempt++) {
        const width = rng.int(spec.minWidth, spec.maxWidth);
        const low = withNegative ? rng.int(spec.lo, -4) : rng.int(Math.max(spec.lo, 2), spec.hi - width + 1);
        const window = range(low, low + width - 1).filter((value) => Math.abs(value) >= 2);
        const targets = rng.shuffle(window).slice(0, 4);
        const negatives = targets.filter((value) => value < 0).length;
        const signsFit = withNegative ? negatives >= 1 && negatives <= 3 : negatives === 0;
        if (signsFit && (!spec.suitable || spec.suitable(targets))) {
            return sorted(targets);
        }
    }
    throw new Error("No targets found");
}

/**
 * Grades 2 to 4: a group is written with one tile per operation. Grades 5 to 8: with one
 * tile in each of four families that have a way to write the value. Null if a group has fewer.
 */
function slotsFor(spec: GradeSpec, level: Level, targets: number[], rng: Rng): Slot[] | null {
    const slots: Slot[] = [];
    for (let group = 0; group < targets.length; group++) {
        const value = targets[group];
        const kindsOf = (key: string) => usableFor(`${level}${key}`, value, () => spec.kinds[key](value));
        let keys: string[];
        if (spec.opsFor) {
            keys = spec.opsFor(value, rng);
        } else {
            const lists = new Map<string, Candidate[]>();
            spec.familiesFor!(value).forEach((family) => {
                const candidates = kindsOf(family);
                if (candidates.length > 0) {
                    lists.set(family, candidates);
                }
            });
            keys = rng.shuffle([...lists.keys()]).slice(0, 4);
            if (keys.length < 4) {
                return null;
            }
        }
        keys.forEach((key) => slots.push({ group, candidates: kindsOf(key), chosen: null }));
    }
    return slots;
}

/** Row-by-row, no row of four is already one group. */
function openingOrder(ids: string[], groupOf: (id: string) => number, rng: Rng): string[] {
    const hasSolvedRow = (order: string[]) =>
        [0, 4, 8, 12].some((start) => {
            const row = order.slice(start, start + 4).map(groupOf);
            return row.every((group) => group === row[0]);
        });
    let order = rng.shuffle(ids);
    for (let attempt = 0; attempt < 50 && hasSolvedRow(order); attempt++) {
        order = rng.shuffle(ids);
    }
    return order;
}

/** Version 1 of the generator, with the expression trees (for the tests that check the arithmetic). */
export function buildBoardV1(seed: string, level: Level): BuiltBoard {
    const rng = createRng(seed);
    const spec = GRADES[level];
    // Drawn once, so a board that needs another try stays the kind it was.
    const withNegative = spec.negativeOneIn > 0 && rng.int(1, spec.negativeOneIn) === 1;
    for (let attempt = 0; attempt < 60; attempt++) {
        const targets = targetsFor(spec, rng, withNegative);
        const slots = slotsFor(spec, level, targets, rng);
        if (!slots || !fill(slots, rng)) {
            continue;
        }
        const groups = targets.map((value, group) => ({
            value,
            nodes: slots.filter((slot) => slot.group === group).map((slot) => slot.chosen!.node),
        }));
        const ids = groups.flatMap((_, group) => range(0, 3).map((i) => tileId(group, i)));
        return { groups, order: openingOrder(ids, (id) => Number(id.slice(1)) >> 2, rng) };
    }
    throw new Error(`No board found for ${seed}`);
}

const tileId = (group: number, index: number): string => `t${group * 4 + index}`;

export function toBoard(seed: string, level: Level, built: BuiltBoard): Board {
    return {
        seed,
        level,
        groups: built.groups.map((group, groupIndex) => ({
            value: group.value,
            tiles: group.nodes.map((node, i) => ({ id: tileId(groupIndex, i), text: display(node), groupIndex })),
        })),
        order: built.order,
    };
}

export function generateV1(seed: string, level: Level): Board {
    return toBoard(seed, level, buildBoardV1(seed, level));
}

/**
 * A change to how boards are made is a new entry here, applying from its start
 * date, so a board already played never changes (ADR 0127 §4). Oldest first.
 */
export const GENERATOR_VERSIONS: { from: string; generate: (seed: string, level: Level) => Board }[] = [
    { from: "2026-10-29", generate: generateV1 },
];

/**
 * The board for a seed. A daily seed ("2026-10-29:g3") uses the generator in
 * force on its date; any other seed (a practice board) uses the newest.
 */
export function generateBoard(seed: string, level: Level): Board {
    const dated = /^(\d{4}-\d{2}-\d{2}):/.exec(seed);
    let version = GENERATOR_VERSIONS[0];
    for (const candidate of GENERATOR_VERSIONS) {
        if (!dated || candidate.from <= dated[1]) {
            version = candidate;
        }
    }
    return version.generate(seed, level);
}

/** A seed for a practice board: never shared, so it may come from Math.random. */
export function practiceSeed(level: Level, random: () => number = Math.random): string {
    return `practice:${level}:${Math.floor(random() * 2 ** 32).toString(36)}`;
}
